import { formatDistanceToNow } from 'date-fns';
import { Gavel, MessageSquare } from 'lucide-react';
import { useState } from 'react';
import { Dialog, DialogTrigger, Header, Heading } from 'react-aria-components';
import { useNavigate } from 'react-router-dom';
import { Button } from '../tailgrids/core/button.js';
import { Popover } from '../tailgrids/core/popover.js';
import { ScrollArea, ScrollAreaViewport, ScrollBar } from '../tailgrids/core/scroll-area.js';
import { BellIcon } from './shell-icons.js';
import { DecisionReviewDialog } from '../approval/DecisionReviewDialog.js';
import { useWaitingOnMe } from '../../hooks/useWaitingOnMe.js';
import type { WaitingOnMeItem } from '../../generated/api/types.gen.js';

/**
 * "Waiting on you": approvals and conversations that need this person, across every module.
 * A conversation opens in place; a decision opens its review dialog.
 * Template example: NextAdmin `components/common/header/notifications.tsx` (commit e0b72a9aa69e).
 */
export function WaitingOnMeMenu() {
  const { data, isError } = useWaitingOnMe('all');
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [reviewing, setReviewing] = useState<number | null>(null);
  const items = data?.items ?? [];
  const count = data?.count ?? 0;

  const openItem = (item: WaitingOnMeItem) => {
    setOpen(false);
    if (item.kind === 'conversation' && item.conversationId !== undefined) {
      navigate(`/conversations/${item.conversationId}`);
    } else if (item.kind === 'decision' && item.decisionId !== undefined) {
      setReviewing(item.decisionId);
    }
  };

  return (
    <>
      <DialogTrigger isOpen={open} onOpenChange={setOpen}>
        <Button
          iconOnly
          appearance="outline"
          aria-label={count > 0 ? `Waiting on you: ${count}` : 'Waiting on you'}
          className="relative size-10 rounded-lg border border-card-border bg-card-background text-icon-primary shadow-xs focus-visible:border-input-primary-focus-border focus-visible:ring-4 focus-visible:ring-input-primary-focus-border/20 [&>svg]:size-auto"
        >
          <BellIcon />
          {count > 0 && (
            <span className="absolute top-2 right-2.75 z-1 size-2 rounded-full bg-error-500">
              <span className="absolute inset-0 -z-1 animate-ping rounded-full bg-error-500 opacity-75" />
            </span>
          )}
        </Button>
        <Popover
          placement="bottom end"
          className="w-84.5 overflow-hidden rounded-2xl border border-border-secondary-alt bg-background-white-secondary p-0 shadow-3xl"
        >
          <Dialog aria-label="Waiting on you" className="outline-none">
            {/* Header */}
            <Header className="flex items-center justify-between border-b border-border-secondary-alt px-5 pt-5 pb-4">
              <Heading slot="title" className="leading-6 font-semibold text-text-primary">
                Waiting on you
              </Heading>
              {count > 0 && <span className="text-xs text-text-tertiary">{count} open</span>}
            </Header>

            <ScrollArea className="h-100 max-h-100">
              <ScrollAreaViewport>
                {isError && (
                  <p className="px-3 py-6 text-center text-sm text-text-tertiary">Could not load. It will retry.</p>
                )}
                {!isError && items.length === 0 && (
                  <p className="px-3 py-6 text-center text-sm text-text-tertiary">Nothing needs you right now.</p>
                )}
                {items.length > 0 && (
                  <section>
                    {/* Group header */}
                    <div className="border-t border-b border-border-primary bg-background-gray-secondary px-5 py-2">
                      <p className="text-xs leading-4 text-text-tertiary uppercase">Needs you</p>
                    </div>
                    {/* List */}
                    <ul className="flex-1 overflow-y-auto px-3 py-2">
                      {items.map((item) => (
                        <li key={item.id}>
                          <button
                            type="button"
                            onClick={() => openItem(item)}
                            className="group flex w-full cursor-pointer gap-3.5 rounded-lg px-3 py-3 transition-colors duration-300 hover:bg-background-gray-secondary_alt"
                          >
                            {/* Icon */}
                            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-border-secondary bg-background-gray-primary text-icon-secondary transition-all duration-300 group-hover:bg-brand-500 group-hover:text-base-white group-hover:shadow-[0_1px_3px_0.5px_rgba(13,13,18,0.08)]">
                              {item.kind === 'decision' ? <Gavel size={18} /> : <MessageSquare size={18} />}
                            </span>

                            {/* Content */}
                            <div className="min-w-0 flex-1 text-start">
                              <p className="truncate text-sm leading-5 font-semibold text-text-primary">{item.title}</p>
                              <p className="mt-1 line-clamp-2 text-xs leading-4 text-text-secondary">{item.subtitle}</p>
                              <p className="mt-2 text-xs leading-4 text-text-tertiary">
                                {formatDistanceToNow(new Date(item.updatedAt), { addSuffix: true })}
                              </p>
                            </div>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}
              </ScrollAreaViewport>
              <ScrollBar />
            </ScrollArea>
          </Dialog>
        </Popover>
      </DialogTrigger>
      <DecisionReviewDialog decisionId={reviewing} onClose={() => setReviewing(null)} />
    </>
  );
}
